import { doc, DocumentData, updateDoc } from 'firebase/firestore'
import * as React from 'react'
import { Data } from 'react-firebase-hooks/firestore/dist/firestore/types'
import { firebaseAuth, firestore } from '../../../config/firebase'
import { Button } from '../../../shared-components/Button'
import { ProgressBar } from '../../../shared-components/ProgressBar'
import { useScreenTracking } from '../../../shared-hooks/useScreenTracking'
import { USERS_COLLECTION } from '../../../shared-hooks/useUserData'
import { useUserSessions } from '../../../shared-hooks/useUserSessions'
import { track } from '../../../utils/analytics'
import { SignupStep } from '../SignupFlow'
import { HowItWorks } from './HowItWorks'

type GeneratingProps = {
  userData: Data<DocumentData, '', ''>
  setSignupStep: (step: SignupStep) => void
}

export const Generating = ({
  userData,
  setSignupStep,
}: GeneratingProps): JSX.Element => {
  useScreenTracking('GeneratingView', 'View generating screen')

  const [progress, setProgress] = React.useState(0)
  const [nextStep, setNextStep] = React.useState<SignupStep | undefined>(
    undefined
  )
  const isDone = React.useRef(false)
  const hasFiredMidpointEvent = React.useRef(false)

  const userSessions = useUserSessions()

  // Everytime firestore data updates, this compoonent will re-render. In this
  // `useEffect` hook, we check whether or not the user has finished generating.
  // Once the user has finished generating, depending on various criteria, we
  // will direct them down various paths. The exact business logic is outlined
  // below:
  //
  // 1. Login in with custom token
  // 2. Check to see if sessions collection is null or empty
  //     a. if yes, display text with link that redirects to app.ridebelay.com
  // 3. Poll for the rates map to exist
  // 4. Check to see if user.rates.base_rate.rate == 0
  //     b. if yes, display text saying a team member will reach out
  // 5. Grab stats data to display from user > stats > 25/50/75%
  // TODO: Move this to a notion page and link it here instead?
  React.useEffect(() => {
    // Skip this block if we've already processed the next steps.
    if (isDone.current) {
      return
    }
    // User must have finished generating and we must have finished loading in
    // the data before we can proceed.
    if (!userData || !userSessions) {
      return
    }

    let nextStep: SignupStep
    if (userSessions.length) {
      isDone.current = true
      nextStep = SignupStep.ExistingAccount
    } else if (userData.rates) {
      isDone.current = true

      if (userData.rates.base_rate.rate === 0) {
        nextStep = SignupStep.InsufficientTripHistory
        const userDocRef = doc(
          firestore,
          USERS_COLLECTION,
          firebaseAuth.currentUser.uid
        )
        updateDoc(userDocRef, { status: 'no_data' })
      } else {
        nextStep = SignupStep.SelectCoverage
      }
    }

    // We only set this timeout one time after we've generated the user data.
    if (isDone.current) {
      setProgress(100)

      if (!hasFiredMidpointEvent.current) {
        track('GeneratingMidpointView', 'View generating midpoint screen')
        hasFiredMidpointEvent.current = true
      }
      mixpanel.identify(userData.email)

      // We have the timeout so the progress bar finishes completely before
      // showing the continue button.
      setTimeout(() => {
        setNextStep(nextStep)
      }, 500)
    }
  })

  React.useEffect(() => {
    const incrementProgress = () =>
      setProgress((prevProgress) => {
        if (isDone.current) {
          return
        }
        // Front load the loading progress to keep the user engaged.
        const randomIncrement = (Math.random() * (100 - prevProgress)) / 2.5
        const newProgress = Math.min(prevProgress + randomIncrement, 99)

        if (newProgress > 50 && !hasFiredMidpointEvent.current) {
          track('GeneratingMidpointView', 'View generating midpoint screen')
          hasFiredMidpointEvent.current = true
        }
        return newProgress
      })

    const intervalId = setInterval(incrementProgress, 1000)
    return () => clearInterval(intervalId)
  }, [])

  const footerComponent = nextStep ? (
    <div className='w-full -mt-3'>
      <Button type='button' onClick={() => setSignupStep(nextStep)}>
        Continue
      </Button>
    </div>
  ) : (
    <>
      <ProgressBar progressPercentage={progress} />
      <p className='text-sm font-extralight text-body-black'>
        Getting your account ready...
      </p>
    </>
  )

  return <HowItWorks>{footerComponent}</HowItWorks>
}
